import React, { createRef, useEffect, useRef, useState } from 'react'
import GoogleMapReact from 'google-map-react'
import { Config } from 'Config'
import { connect } from 'react-redux'
import {
  StationIcon,
  StationClusterIcon,
  StationMapComponent,
  IFFilter,
  SearchBox,
} from 'Components'
import StationSelectors from 'Stores/Station/Selectors'
import StationActions from 'Stores/Station/Actions'
import AuthSelectors from 'Stores/Auth/Selectors'
import Popover from '@mui/material/Popover'
import Styles from './IFStationsMap.module.css'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { Colors } from 'Theme'
import RequestState from 'Enums/RequestState'
import { useLocation } from 'react-router-dom'
import { stationsMapFilter } from 'Constants/Filters'
import { encodeObjectToBase64 } from 'Utils/base64functions'

const IFStationsMap = ({
  stationClusters,
  stationClustersRequestState,
  fetchStationMapClusters,
  stationsFilter,
  fetchStationCities,
  setStationsFilter,
  width = '70%',
  height = '80vh',
  operator,
  stationMapClustersSearch,
  fetchStationMapClustersSearch,
  setStationMapClustersSearchWord,
  clearStationMapClustersSearchWord,
  clearStationMapClustersSearch,
}) => {
  const defaultProps = {
    center: {
      lat: 26.923829355301727,
      lng: 30.056246323840774,
    },
    zoom: 7,
    bounds: ['-180', '-85', '180', '85'],
  }

  const { t } = useTranslation()
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [bounds, setBounds] = useState(null)
  const [googleMap, setGoogleMap] = useState(null)
  const [zoom, setZoom] = useState(7)
  const [center, setCenter] = useState()
  const [stationComponentChargePoints, setStationComponentChargePoints] =
    useState([])
  const [anchorEl, setAnchorEl] = useState(null)
  const stationRefs = useRef()
  const mapRef = useRef()
  const filterStationMapRef = useRef()
  const previousFilterValues = useRef({ stationsFilter })
  const location = useLocation()
  useEffect(() => {
    const filter = encodeObjectToBase64(stationsMapFilter)
    fetchStationMapClusters(defaultProps.zoom, defaultProps.bounds, filter)
  }, [])
  useEffect(() => {
    if (operator) fetchStationCities()
  }, [operator])
  useEffect(() => {
    if (location.pathname === '/map') {
      setStationsFilter(stationsMapFilter)
      filterStationMapRef?.current?.clearFilter()
      if (!isFirstLoad) {
        const filter = encodeObjectToBase64(stationsMapFilter)
        fetchStationMapClusters(defaultProps.zoom, defaultProps.bounds, filter)
        fetchStationCities()
      }
    }
  }, [location])

  useEffect(() => {
    if (bounds) {
      const filter = encodeObjectToBase64(stationsFilter)
      if (!isFirstLoad) fetchStationMapClusters(zoom, bounds, filter)
    }
  }, [bounds])

  useEffect(() => {
    const filter = encodeObjectToBase64(stationsFilter)
    if (!isFirstLoad)
      fetchStationMapClusters(defaultProps.zoom, defaultProps.bounds, filter)
  }, [stationsFilter])

  const handleStationClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleStationClose = () => {
    setAnchorEl(null)
  }

  const stationPopoverOpen = Boolean(anchorEl)
  const stationPopoverID = stationPopoverOpen ? 'simple-popover' : undefined

  const fetchStationMapClustersSearchHandler = () => {
    fetchStationMapClustersSearch(zoom, defaultProps.bounds, stationsFilter)
  }

  const fetchStationMapClustersSearchRef = useRef(
    fetchStationMapClustersSearchHandler,
  )

  useEffect(() => {
    fetchStationMapClustersSearchRef.current =
      fetchStationMapClustersSearchHandler
  })

  const getClusterCoordinates = (cluster) => {
    if (cluster?.geometry?.coordinates) {
      return cluster.geometry.coordinates
    }

    if (
      cluster?.properties?.cluster &&
      cluster.properties.clusterChildrenLocations?.length
    ) {
      return cluster.properties.clusterChildrenLocations[0].coordinates
    }

    return null
  }

  const setZoomAndCenteringAuto = () => {
    if (
      googleMap &&
      window.google &&
      window.google.maps &&
      typeof window.google.maps.LatLngBounds === 'function' &&
      stationClusters.length !== 0
    ) {
      const boundsTemp = new window.google.maps.LatLngBounds()
      stationClusters.forEach((cluster) => {
        if (stationClusters.length === 1 && cluster.properties.cluster) {
          cluster.properties.clusterChildrenLocations.forEach((child) => {
            let myLatLng = new window.google.maps.LatLng(
              child.coordinates[1],
              child.coordinates[0],
            )
            boundsTemp.extend(myLatLng)
          })
        } else {
          const coordinates = getClusterCoordinates(cluster)
          if (!coordinates) return

          let myLatLng = new window.google.maps.LatLng(
            coordinates[1],
            coordinates[0],
          )
          boundsTemp.extend(myLatLng)
        }
      })
      if (googleMap !== null) {
        googleMap.fitBounds(boundsTemp, 114)
        googleMap.setCenter(boundsTemp.getCenter())
      }
    } else {
      setCenter(defaultProps.center)
    }
  }

  useEffect(() => {
    if (stationClusters.length !== 0) {
      setIsFirstLoad(false)
    }
    let newStationFilter = stationsFilter
    if (previousFilterValues.current !== newStationFilter) {
      setZoomAndCenteringAuto()
      previousFilterValues.current = newStationFilter
    }
  }, [stationClusters])

  useEffect(() => {
    if (googleMap !== null) {
      setTimeout(() => {
        setZoomAndCenteringAuto()
      }, 100)
    }
  }, [googleMap])

  const formatCoordinates = (coord) => {
    return Number(coord).toFixed(5)
  }

  return (
    <div style={{ height: height, width: width }}>
      <GoogleMapReact
        ref={mapRef}
        onGoogleApiLoaded={({ map }) => {
          setGoogleMap(map)
          mapRef.current = map
        }}
        bootstrapURLKeys={{
          key: Config.REACT_APP_API_KEY,
        }}
        defaultZoom={defaultProps.zoom}
        defaultCenter={defaultProps.center}
        center={center}
        yesIWantToUseGoogleMapApiInternals
        onChange={({ zoom: mapZoom, bounds: mapBounds }) => {
          if (mapZoom !== zoom) {
            setZoom(mapZoom)
          }
          if (
            !bounds ||
            bounds[0] !== mapBounds[0] ||
            bounds[1] !== mapBounds[1] ||
            bounds[2] !== mapBounds[2] ||
            bounds[3] !== mapBounds[3]
          ) {
            setBounds([
              mapBounds.nw.lng,
              mapBounds.se.lat,
              mapBounds.se.lng,
              mapBounds.nw.lat,
            ])
          }
        }}
        options={{
          fullscreenControl: false,
          maxZoom: 20,
          clickableIcons: false,
        }}
      >
        {stationClusters.map((cluster, index) => {
          const coordinates = getClusterCoordinates(cluster)
          if (!coordinates) return null

          const [longitude, latitude] = coordinates
          const {
            cluster: isCluster,
            point_count: pointCount,
            maxPower,
            status,
          } = cluster.properties
          const key = `${formatCoordinates(latitude)},${formatCoordinates(
            longitude,
          )}`
          stationRefs.current = {
            ...stationRefs.current,
            [key]: createRef(),
          }

          if (isCluster) {
            return (
              <StationClusterIcon
                key={`cluster-${cluster.id}`}
                lat={latitude}
                lng={longitude}
                clusterCount={pointCount}
                maxChargingSpeed={cluster.properties.clusterMaxPower}
                stationStatus={cluster.properties.clusterStationStatus}
                onClick={() => {
                  const expansionZoom = Math.min(
                    cluster.properties.clusterExpansionZoom,
                    20,
                  )
                  mapRef.current.setZoom(expansionZoom)
                  mapRef.current.panTo({
                    lat: Number(latitude),
                    lng: Number(longitude),
                  })
                }}
              />
            )
          }
          return (
            <StationIcon
              key={`station-${index}`}
              ref={stationRefs.current[key]}
              lat={latitude}
              lng={longitude}
              maxChargingSpeed={maxPower}
              stationStatus={status}
              onClick={(event) => {
                handleStationClick(event)
                setStationComponentChargePoints(cluster.properties)
              }}
              addMapOffset={true}
              isOffline={cluster.properties.isStationOffline}
            />
          )
        })}
      </GoogleMapReact>
      <div className={Styles.TopDetailsContainer}>
        <div className={Styles.SearchBoxContainer}>
          <SearchBox
            mapRef={mapRef}
            stationMapClustersSearch={stationMapClustersSearch}
            fetchStationMapClustersSearch={() =>
              fetchStationMapClustersSearchRef.current()
            }
            setStationMapClustersSearchWord={(word) =>
              setStationMapClustersSearchWord(word)
            }
            showPopover={(key) => {
              const formatKeyToFiveDecimals = (key) => {
                const [lat, lng] = key.split(',').map(Number)
                return `${lat.toFixed(5)},${lng.toFixed(5)}`
              }
              const formattedKey = formatKeyToFiveDecimals(key)
              stationRefs?.current?.[formattedKey]?.current?.click()
            }}
            hidePopover={() => setAnchorEl(null)}
            clearStationMapClustersSearchWord={() =>
              clearStationMapClustersSearchWord()
            }
            clearStationMapClustersSearch={() => clearStationMapClustersSearch()}
          />
        </div>

        <div
          className={Styles.MapFilter}
          style={{ boxShadow: `${Colors.BoxShadow} 0px 0px 4px` }}
        >
          <div className={Styles.StationsFilter}>
            <IFFilter
              ref={filterStationMapRef}
              key={'IFFilter_Stations'}
              onFilterChange={(newFilter) => {
                setStationsFilter(newFilter)
                setAnchorEl(null)
              }}
              filters={stationsFilter}
              textFieldPlaceholder={t('StationsMap.StationsFilterPlaceholder')}
              showLoadingIndicator={
                stationClustersRequestState === RequestState.LOADING
              }
              operator={operator}
            />
          </div>
        </div>
      </div>

      <Popover
        id={stationPopoverID}
        open={stationPopoverOpen}
        anchorEl={anchorEl}
        disableEnforceFocus={true}
        onClose={handleStationClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        PaperProps={{ sx: { borderRadius: '10px' } }}
      >
        <StationMapComponent stationMapData={stationComponentChargePoints} />
      </Popover>
    </div>
  )
}

function mapDispatchToProps(dispatch) {
  return {
    fetchStationMapClusters: (zoom, bounds, filter) =>
      dispatch(StationActions.fetchStationMapClusters(zoom, bounds, filter)),
    fetchStationCities: () => dispatch(StationActions.fetchStationCities()),
    setStationsFilter: (filter) =>
      dispatch(StationActions.setStationsMapFilter(filter)),
    fetchStationMapClustersSearch: (zoom, bounds, filter) =>
      dispatch(
        StationActions.fetchStationMapClustersSearch(zoom, bounds, filter),
      ),
    setStationMapClustersSearchWord: (searchWord) =>
      dispatch(StationActions.setStationMapClustersSearchWord(searchWord)),
    clearStationMapClustersSearchWord: () =>
      dispatch(StationActions.clearStationMapClustersSearchWord()),
    clearStationMapClustersSearch: () =>
      dispatch(StationActions.clearStationMapClustersSearch()),
  }
}

const mapStateToProps = (state) => ({
  stationClusters: StationSelectors.getStationMapClusters(state),
  stationsFilter: StationSelectors.getStationMapFilters(state),
  stationClustersRequestState:
    StationSelectors.getFetchStationMapClustersRequestState(state),
  operator: AuthSelectors.getOperator(state),
  stationMapClustersSearch: StationSelectors.getStationMapClustersSearch(state),
})

IFStationsMap.propTypes = {
  stationClusters: PropTypes.arrayOf(PropTypes.object),
  stationClustersRequestState: PropTypes.number,
  fetchStationMapClusters: PropTypes.func,
  stationsFilter: PropTypes.arrayOf(PropTypes.object),
  fetchStationCities: PropTypes.func,
  setStationsFilter: PropTypes.func,
  width: PropTypes.string,
  height: PropTypes.string,
}

export default connect(mapStateToProps, mapDispatchToProps)(IFStationsMap)
